import React, { useCallback, useEffect, useState } from "react";
// import { useParams } from "react-router-dom";
import {
  Breadcrumbs,
  BreadbrumbItem,
  ContentContainer,
  ContentSection,
  StatusText,
  LargeTextValue,
  CompactInt,
  StyledTextContent,
} from "../shared/Components";
import {
  Campaign,
  CampaignLabelingJob,
  CampaignPrefilteringJob,
} from "../../appstore/models";
import {
  Container,
  PieChart,
  Table,
  Box,
  Button,
  CollectionPreferences,
  ColumnLayout,
  Header,
  Link,
  Pagination,
  TextFilter,
} from "@awsui/components-react";
import styled from "styled-components";
import * as Humanize from "humanize-plus";
import CampaignMap from "./CampaignMap";
import CampaignPrefilteringJobSummary from "./CampaignPrefilteringJobSummary";
import CampaignLabelingJobSummary from "./CampaignLabelingJobSummary";
import CampaignManifestSummary from "./CampaignManifestSummary";
import { useStoreState } from "../../appstore";
import { useParams } from "react-router";
import { describeCampaign } from "../../common/api";
import appConfig from "../../common/appConfig";
import { splitS3Uri } from "../../common/helpers";

const TextLabel = styled.div`
  color: #545b64;
`;

const ImagesSummaryTable = (props: { jobDescription: Campaign }) => {
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const { jobDescription } = props;
  const pageSize = 8;

  return (
    <ContentSection>
      <Table
        trackBy="name"
        columnDefinitions={[
          {
            id: "filename",
            header: "Filename",
            cell: (e) => e.Filename,
            sortingField: "filename",
          },
          {
            id: "status",
            header: "Status",
            //cell: (e) => e.status,
            cell: (e) => "Complete",
            sortingField: "status",
          },
          {
            id: "s3Uri",
            header: "S3 URI",
            cell: (e) => e.ImageS3URI,
            sortingField: "s3Uri",
          },
          {
            id: "size",
            header: "Size",
            cell: (e) => Humanize.fileSize(e.ImageSizeBytes),
            sortingField: "size",
          },
          {
            id: "dimensions",
            header: "Dimensions",
            cell: (e) => e.Dimensions.join(" x "),
            sortingField: "dimensions",
          },

          {
            id: "chipCount",
            header: "Chip Count",
            cell: (e) => e.ChipCount,
            sortingField: "chipCount",
          },
          /*
          {
            id: "chipsLabeled",
            header: "Chips Labeled",
            cell: (e) => "TODO",
            sortingField: "chipsLabeled",
          },
          {
            id: "featuresLabeled",
            header: "Features Labeled",
            cell: (e) => "TODO",
            sortingField: "featuresLabeled",
          },
          */

          {
            id: "coordinateSystem",
            header: "Coordinate System",
            cell: (e) => e.CoordinateSystem,
            sortingField: "coordinateSystem",
          },
          {
            id: "center",
            header: "center",
            cell: (e) => e.Center.join(", "),
            sortingField: "center",
          },
        ]}
        items={jobDescription.Files.slice(
          (currentPageIndex - 1) * pageSize,
          currentPageIndex * pageSize
        )}
        loadingText="Loading images"
        visibleColumns={[
          "filename",
          "status",
          "s3Uri",
          "size",
          "dimensions",
          "chipCount",
          "chipsLabeled",
          "featuresLabeled",
          "coordinateSystem",
          "center",
        ]}
        empty={
          <Box textAlign="center" color="inherit">
            <b>No images</b>
            <Box padding={{ bottom: "s" }} variant="p" color="inherit">
              No images to display.
            </Box>
          </Box>
        }
        filter={
          <TextFilter filteringText="" filteringPlaceholder="Find resources" />
        }
        header={<Header>Images</Header>}
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            onChange={({ detail }) =>
              setCurrentPageIndex(detail.currentPageIndex)
            }
            pagesCount={Math.floor(jobDescription.Files.length / pageSize)}
            ariaLabels={{
              nextPageLabel: "Next page",
              previousPageLabel: "Previous page",
              pageLabel: (pageNumber) => `Page ${pageNumber} of all pages`,
            }}
          />
        }
        preferences={
          <CollectionPreferences
            title="Preferences"
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            preferences={{
              pageSize: pageSize,
              visibleContent: ["variable", "value", "type", "description"],
            }}
            pageSizePreference={{
              title: "Select page size",
              options: [
                { value: 10, label: "10 resources" },
                { value: 20, label: "20 resources" },
              ],
            }}
            visibleContentPreference={{
              title: "Select visible content",
              options: [
                {
                  label: "Main distribution properties",
                  options: [
                    {
                      id: "variable",
                      label: "Variable name",
                      editable: false,
                    },
                    { id: "value", label: "Text value" },
                    { id: "type", label: "Type" },
                    {
                      id: "description",
                      label: "Description",
                    },
                  ],
                },
              ],
            }}
          />
        }
      />
    </ContentSection>
  );
};

const ImagesSummary = (props: { campaign: Campaign }) => {
  const { campaign } = props;
  return (
    <ContentSection>
      <Container header={<Header variant="h2">Images Summary</Header>}>
        <ColumnLayout columns={3}>
          <StyledTextContent>
            <TextLabel>Image Count</TextLabel>

            <LargeTextValue>
              <CompactInt value={campaign.ImageCount} />
            </LargeTextValue>
          </StyledTextContent>
          <StyledTextContent>
            <TextLabel>Total Chip Count</TextLabel>
            <LargeTextValue>
              <CompactInt value={campaign.TotalChipCount} />
            </LargeTextValue>
          </StyledTextContent>
          <StyledTextContent>
            <TextLabel>Total Image Size</TextLabel>
            <LargeTextValue>
              {Humanize.fileSize(campaign.InputSizeBytes)}
            </LargeTextValue>
          </StyledTextContent>
        </ColumnLayout>
      </Container>
    </ContentSection>
  );
};

const FeaturesLabeledSummary = (props: {
  geoLabelingJob: CampaignLabelingJob;
}) => {
  const { geoLabelingJob: jobDescription } = props;
  const labels = Object.keys(jobDescription.FeaturesClassedLabeledCount);
  const data = labels.map((label) => ({
    title: label,
    value: jobDescription.FeaturesClassedLabeledCount[label],
    lastUpdate: "",
  }));
  return (
    <ContentSection>
      <Container header={<Header variant="h2">Labeled Features</Header>}>
        <ColumnLayout columns={1}>
          <div>
            <PieChart
              data={data}
              detailPopoverContent={(datum, sum) => [
                { key: "Feature count", value: Humanize.intComma(datum.value) },
                {
                  key: "Percentage",
                  value: `${((datum.value / sum) * 100).toFixed(0)}%`,
                },
              ]}
              segmentDescription={(datum, sum) =>
                `${Humanize.compactInteger(datum.value)} units, ${(
                  (datum.value / sum) *
                  100
                ).toFixed(0)}%`
              }
              i18nStrings={{
                detailsValue: "Value",
                detailsPercentage: "Percentage",
                filterLabel: "Filter displayed data",
                filterPlaceholder: "Filter data",
                filterSelectedAriaLabel: "selected",
                detailPopoverDismissAriaLabel: "Dismiss",
                legendAriaLabel: "Legend",
                chartAriaRoleDescription: "pie chart",
                segmentAriaRoleDescription: "segment",
              }}
              ariaDescription="Pie chart showing how many resources are currently in which state."
              ariaLabel="Pie chart"
              errorText="Error loading data."
              loadingText="Loading chart"
              recoveryText="Retry"
              size="medium"
              empty={
                <Box textAlign="center" color="inherit">
                  <b>No data available</b>

                  <Box variant="p" color="inherit">
                    There is no data available
                  </Box>
                </Box>
              }
              noMatch={
                <Box textAlign="center" color="inherit">
                  <b>No matching data</b>
                  <Box variant="p" color="inherit">
                    There is no matching data to display
                  </Box>
                  <Button>Clear filter</Button>
                </Box>
              }
            />
          </div>
        </ColumnLayout>
      </Container>
    </ContentSection>
  );
};

const DescribeCampaign = () => {
  let { jobName } = useParams<{
    jobName: string;
  }>();
  const [campaign, setCampaign] = useState<Campaign | undefined>();
  useState<CampaignPrefilteringJob | undefined>();
  const [errorMessage, setErrorMessage] = useState("");
  const selectedGeoLabelingJobName = useStoreState(
    (s) => s.selectedGeoLabelingJobName
  );
  const selectedGeoLabelingJob = useStoreState((s) =>
    selectedGeoLabelingJobName
      ? s.geoLabelingJobs.byJobName[selectedGeoLabelingJobName]
      : undefined
  );
  const loadCampaign = useCallback(async () => {
    if (campaign && campaign.JobName === jobName) {
      return;
    }
    const newCampaign = await describeCampaign(jobName);

    if (!newCampaign) {
      setErrorMessage(`Could not find job with name ${jobName}`);
      return;
    }
    setCampaign(newCampaign);
  }, [jobName, campaign]);

  useEffect(() => {
    loadCampaign();
  }, [loadCampaign]);

  if (!campaign) {
    return <div>"Loading..."</div>;
  }
  const items: BreadbrumbItem[] = [
    {
      text: "Geospatial Data Labeling",
      href: "/",
    },
    {
      text: "Labeling Campaigns",
      href: "/",
    },
    {
      text: campaign.JobName,
      href: `#/geo-campaigns/${campaign.JobName}`,
    },
  ];

  if (errorMessage) {
    <ContentContainer>
      <Breadcrumbs items={items} />
      <div>
        <Header variant="h1">{errorMessage}</Header>
      </div>
    </ContentContainer>;
  }

  const [inputDataS3Bucket, inputDataS3Key] = splitS3Uri(
    campaign.InputDataS3Prefix
  );
  const inputDataS3PrefixConsoleUrl = new URL(
    `https://${appConfig.consoleHostname}/s3/buckets/${inputDataS3Bucket}`
  );
  inputDataS3PrefixConsoleUrl.searchParams.append("prefix", inputDataS3Key);

  const [outputDataS3Bucket, outputDataS3Key] = splitS3Uri(
    campaign.OutputDataS3Prefix
  );
  const outputDataS3PrefixConsoleUrl = new URL(
    `https://${appConfig.consoleHostname}/s3/buckets/${outputDataS3Bucket}`
  );
  outputDataS3PrefixConsoleUrl.searchParams.append("prefix", outputDataS3Key);

  const createCampaignStepFunctionExecutionConsoleUrl = new URL(
    `https://${appConfig.consoleHostname}/states/home?region=${appConfig.awsRegion}#/executions/details/${campaign.CreateCampaignStepFunctionExecutionArn}`
  );

  const campaignStatusCode =
    campaign.Status === "Created"
      ? "success"
      : campaign.Status === "Error"
      ? "error"
      : "in_progress";
  const campaignStatus = (
    <StatusText status={campaignStatusCode}>{campaign.Status}</StatusText>
  );

  return (
    <ContentContainer>
      <Breadcrumbs items={items} />
      <div>
        <Header variant="h1">{campaign.JobName}</Header>
        <ContentSection>
          <Container
            header={<Header variant="h2">Campaign Configuration</Header>}
          >
            <ColumnLayout columns={3}>
              <div>
                <StyledTextContent>
                  <strong>Status</strong>
                  {campaignStatus}
                </StyledTextContent>

                <StyledTextContent>
                  <strong>Tile Size</strong>
                  <p>
                    {campaign.TileSize} x {campaign.TileSize}
                  </p>
                </StyledTextContent>
                <StyledTextContent>
                  <strong>Date Created</strong>
                  <p>{new Date(campaign.DateCreated).toString()}</p>
                </StyledTextContent>
              </div>
              <div>
                <StyledTextContent>
                  <strong>Input Data S3 Prefix</strong>
                  <p>
                    <Link
                      href={inputDataS3PrefixConsoleUrl.href}
                      target="_blank"
                    >
                      {campaign.InputDataS3Prefix}
                    </Link>
                  </p>
                </StyledTextContent>
                <StyledTextContent>
                  <strong>Output Data S3 Prefix</strong>
                  <p>
                    <Link
                      href={outputDataS3PrefixConsoleUrl.href}
                      target="_blank"
                    >
                      {campaign.OutputDataS3Prefix}
                    </Link>
                  </p>
                </StyledTextContent>
              </div>
              <div>
                <StyledTextContent>
                  <strong>Create Campaign Step Function Execution Arn</strong>
                  <p>
                    {campaign.CreateCampaignStepFunctionExecutionArn ? (
                      <Link
                        href={
                          createCampaignStepFunctionExecutionConsoleUrl.href
                        }
                        target="_blank"
                      >
                        {campaign.CreateCampaignStepFunctionExecutionArn}
                      </Link>
                    ) : (
                      "-"
                    )}
                  </p>
                </StyledTextContent>
              </div>
            </ColumnLayout>
          </Container>
        </ContentSection>
        <ImagesSummary campaign={campaign} />
        <CampaignPrefilteringJobSummary campaign={campaign} />
        <CampaignManifestSummary campaign={campaign} />
        <CampaignLabelingJobSummary campaign={campaign} />
        {selectedGeoLabelingJob ? (
          <FeaturesLabeledSummary geoLabelingJob={selectedGeoLabelingJob} />
        ) : null}

        <ImagesSummaryTable jobDescription={campaign} />
        <CampaignMap campaign={campaign} />
      </div>
    </ContentContainer>
  );
};

export default DescribeCampaign;
